import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { localizedStrings } from '@core/strings'
import { useModal } from '@common/GlobalModal'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LoadingButton,
  TextField,
} from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import { ModalDialogContainer } from '@common/modal'
import { useToast } from '@core/toast'
import { useRenameRegisterItemAttachment } from '@core/react-query/features/registers/registers/register/items/item'

type Props = {
  registerId: number
  registerItemId: number
  attachmentId: number
  initialName: string
}

type FormSchema = {
  name: string
}

export const RenameRegisterItemAttachmentModal: React.FC<Props> = ({
  registerId,
  registerItemId,
  attachmentId,
  initialName,
}) => {
  const { hideModal } = useModal()
  const toast = useToast()
  const renameAttachment = useRenameRegisterItemAttachment()

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<FormSchema>({
    defaultValues: { name: initialName },
  })

  const onSubmit = ({ name }: FormSchema) => {
    renameAttachment.mutate(
      { registerId, registerItemId, attachmentId, name: name.trim() },
      {
        onSuccess: () => {
          toast.success(localizedStrings.fileRenamed)
          hideModal()
        },
        onError: (e) => toast.error(e.message),
      },
    )
  }

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.renameFile}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}
      >
        <Controller
          name='name'
          control={control}
          rules={{
            required: true,
            validate: (value) => value.trim().length > 0,
          }}
          render={({ field, fieldState }) => (
            <TextField
              label={localizedStrings.documentName}
              error={!!fieldState.error}
              sx={{ mt: 1 }}
              fullWidth
              {...field}
            />
          )}
        />
      </DialogContent>

      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button variant='outlined' onClick={hideModal}>
          {localizedStrings.cancel}
        </Button>
        <LoadingButton
          variant='contained'
          onClick={handleSubmit(onSubmit)}
          disabled={!isDirty || renameAttachment.isLoading}
          loading={renameAttachment.isLoading}
        >
          {localizedStrings.submit}
        </LoadingButton>
      </DialogActions>
    </ModalDialogContainer>
  )
}
